// Per-user permission checks for the SPA.
//
// The server is the gate (it re-reads the user row on every request); this
// only decides what to SHOW. A user holds a permission through their role
// (`permissions`), or because an admin granted it to them by name
// (`extra_permissions`, edited in User Management). Admins hold every
// grantable permission implicitly.

export const AUDIENCES_EXPORT = 'audiences:export';
export const AUDIENCES_EXPORT_NATIONAL = 'audiences:export:national';

// Mirrors backend auth.models.GRANTABLE_PERMISSIONS: the permissions an
// admin may give one named user without making them an admin. `requires`
// marks a grant that only means something on top of another one: User
// Management shows it under that one, ticks the base with it, and drops it
// when the base is unticked.
export const GRANTABLE_PERMISSIONS = [
  {
    key: AUDIENCES_EXPORT,
    label: 'Audience extracts',
    badge: 'Audience extracts',
    description:
      "See the ops dashboard's approved marketing audiences and download the hashed Meta / Google files. "
      + 'Without National, only for their own regions and offices (Access Restrictions).',
  },
  {
    key: AUDIENCES_EXPORT_NATIONAL,
    requires: AUDIENCES_EXPORT,
    label: 'National (all regions and offices)',
    badge: 'Audiences: national',
    description:
      'National access to audience extracts: all regions and offices. Otherwise users see only their own regions/offices.',
  },
];

// A grant that implies another for what the SPA shows: the server opens the
// audience routes to either grant.
const IMPLIED_BY = {
  [AUDIENCES_EXPORT]: [AUDIENCES_EXPORT_NATIONAL],
};

export function hasPermission(user, permission) {
  if (!user) return false;
  if (user.role === 'admin') return true;
  const held = [...(user.extra_permissions || []), ...(user.permissions || [])];
  return [permission, ...(IMPLIED_BY[permission] || [])].some((p) => held.includes(p));
}

// Toggle one grantable permission in a list, keeping dependent grants
// consistent: ticking a grant ticks what it requires; unticking a grant
// unticks what requires it.
export function toggleGrant(current, key, on) {
  const set = new Set(current || []);
  if (on) {
    set.add(key);
    const perm = GRANTABLE_PERMISSIONS.find((p) => p.key === key);
    if (perm?.requires) set.add(perm.requires);
  } else {
    set.delete(key);
    GRANTABLE_PERMISSIONS.filter((p) => p.requires === key).forEach((p) => set.delete(p.key));
  }
  return [...set];
}
